import React, { useState, useEffect, useCallback } from "react";
import { DialogComponent } from "../../../Components";
import { useTranslation } from "react-i18next";
import { GetParams } from "../../../Helper";
import { AI_PropertyMarketing  } from "../../../Services/AIServices/AIServices";

const AIPropertyMarketingContentGeneratorComponent = ({ onOpen, onClose, onSave }) => {
  const parentTranslationPath = "Shared";
  const translationPath = "";
  const { t } = useTranslation(parentTranslationPath);

  const [englishTitle, setEnglishTitle] = useState("");
  const [englishDescription, setEnglishDescription] = useState("");
  const [arabicTitle, setArabicTitle] = useState("");
  const [arabicDescription, setArabicDescription] = useState("");
  const [localAreaDescription, setLocalAreaDescription] = useState("");
  const [developerDescription, setDeveloperDescription] = useState("");
  const [englishAddress, setEnglishAddress] = useState("");
  const [arabicAddress, setArabicAddress] = useState("");
  const [additionalTextSuggestions, setAdditionalTextSuggestions] = useState("");
  const [isGenerating, setIsGenerating] = useState(true);
  const [error, setError] = useState("");
  const [showRegenerate, setShowRegenerate] = useState(false);
  const [isGenerationComplete, setIsGenerationComplete] = useState(false);
  const [data, setData] = useState(false);

  const GetData = useCallback(async () => {
    const res = await AI_PropertyMarketing(+GetParams("id"));
    if (!(res && res.status && res.status !== 200)) {
       setData(res);
    } else {
      setData(false);
      setError('Services have Error Or Content not found')
      setEnglishTitle("");
      setEnglishDescription("");
      setArabicTitle("");
      setArabicDescription("");
      setLocalAreaDescription("");
      setDeveloperDescription("");
      setEnglishAddress("");
      setArabicAddress("");
      setAdditionalTextSuggestions("");
      setShowRegenerate(true);
      setIsGenerationComplete(false);
      setIsGenerating(false);
      setData(false);
    }
  });

  const fetchContent = async (language, field, result) => {
    try {
      if (!result) {
        return "";
      }

      const fieldMapping = {
        englishTitle: ["English Marketing Title", "EnglishTitle"],
        englishDescription: ["English Marketing Description", "EnglishMarketing Description"],
        arabicTitle: ["Arabic Marketing Title", "ArabicTitle"],
        arabicDescription: ["Arabic Marketing Description", "ArabicMarketing Description"],
        localAreaDescription: ["Local Area Description", "LocalAreaDescription"],
        developerDescription: ["Developer Description", "DeveloperDescription"],
        englishAddress: ["English Address Line 1", "EnglishAddressLine1"],
        arabicAddress: ["Arabic Address Line 1", "ArabicAddressLine1"],
        additionalTextSuggestions: ["Additional Text Suggestions", "AdditionalTextSuggestions"]
      };

      const fieldKeys = fieldMapping[field];

      for (let key of fieldKeys) {
        if (result[key]) {
          return result[key];
        }
      }

      console.warn(`Field not found: ${field}`);
      return "";
    } catch (error) {
      console.error("Error fetching content:", error);
      return "An error occurred while fetching content.";
    }
  };

  useEffect(() => {
    GetData();
  }, []);

  useEffect(() => {
    if (data !== false) {
      generateContent("englishTitle");
      generateContent("englishDescription");
    }
  }, [data]);

  const generateContent = async (field) => {
    setError("");

    try {
      const content = await fetchContent("english", field, data);
      if (content) {
        simulateTypingEffect(content, field, () => {
          if (field === "englishDescription") {
            generateContent("arabicTitle");
            generateContent("arabicDescription");
          }
          if (field === "arabicDescription") {
            generateContent("localAreaDescription");
            generateContent("developerDescription");
            generateContent("englishAddress");
            generateContent("arabicAddress");
            generateContent("additionalTextSuggestions");
          }
        });
      }
    } catch (err) {
      setError(
        "AI content generation is currently unavailable. Please try again later."
      );
      setIsGenerating(false);
    }
  };

  const simulateTypingEffect = (text, field, onComplete) => {
    if (typeof text !== "string") {
      console.error("Invalid text provided to simulateTypingEffect.");
      return;
    }

    let index = 0;
    const setState =
      field === "englishTitle"
        ? setEnglishTitle
        : field === "englishDescription"
        ? setEnglishDescription
        : field === "arabicTitle"
        ? setArabicTitle
        : field === "arabicDescription"
        ? setArabicDescription
        : field === "localAreaDescription"
        ? setLocalAreaDescription
        : field === "developerDescription"
        ? setDeveloperDescription
        : field === "englishAddress"
        ? setEnglishAddress
        : field === "arabicAddress"
        ? setArabicAddress
        : setAdditionalTextSuggestions;

    const typingInterval = setInterval(() => {
      try {
        if (index < text.length) {
          setState((prevText) => {
            const newText = prevText + text[index];

            if (
              field === "englishDescription" ||
              field === "arabicDescription" ||
              field === "localAreaDescription" ||
              field === "developerDescription" ||
              field === "additionalTextSuggestions"
            ) {
              const textarea = document.querySelector(
                `textarea[data-field="${field}"]`
              );
              if (textarea) {
                textarea.value = newText;
                textarea.scrollTop = textarea.scrollHeight;
              }
            }
            return newText;
          });

          index++;
        } else {
          clearInterval(typingInterval);

          if (field === "additionalTextSuggestions") {
            setIsGenerating(false);
            setIsGenerationComplete(true);
            setShowRegenerate(true);
          }

          if (onComplete) {
            onComplete();
          }
        }
      } catch (error) {
        console.error("Error occurred while typing:", error);
        clearInterval(typingInterval);
      }
    }, 0);
  };

  const handleSave = () => {
    onSave({
      englishTitle,
      englishDescription,
      arabicTitle,
      arabicDescription,
      localAreaDescription,
      developerDescription,
      englishAddress,
      arabicAddress,
      additionalTextSuggestions
    });
    onClose();
  };

  const handleRegenerate = () => {
    setEnglishTitle("");
    setEnglishDescription("");
    setArabicTitle("");
    setArabicDescription("");
    setLocalAreaDescription("");
    setDeveloperDescription("");
    setEnglishAddress("");
    setArabicAddress("");
    setAdditionalTextSuggestions("");
    setShowRegenerate(false);
    setIsGenerationComplete(false);
    setIsGenerating(true);
    setData(false);
    GetData();
  };

  const handleEdit = (e, field) => {
    if (isGenerating) {
      e.preventDefault();
      return;
    }
    if (field === "englishTitle") setEnglishTitle(e.target.value);
    if (field === "englishDescription") setEnglishDescription(e.target.value);
    if (field === "arabicTitle") setArabicTitle(e.target.value);
    if (field === "arabicDescription") setArabicDescription(e.target.value);
    if (field === "localAreaDescription") setLocalAreaDescription(e.target.value);
    if (field === "developerDescription") setDeveloperDescription(e.target.value);
    if (field === "englishAddress") setEnglishAddress(e.target.value);
    if (field === "arabicAddress") setArabicAddress(e.target.value);
    if (field === "additionalTextSuggestions") setAdditionalTextSuggestions(e.target.value);
  };

  return (
    <>
      <DialogComponent
        isOpen={onOpen}
        titleText={
          t(`${translationPath}AiContentCreatorTitle`) ||
          "✨ Ai Content Creator"
        }
        saveText={t(`${translationPath}Save`) || "Save"}
        cancelText={t(`${translationPath}Cancel`) || "Cancel"}
        dialogContent={
          <div className="AIMarketingContentGenerator-wrapper">
            <div className="modal-content">
              {error && <div className="error">{error}</div>}
              {isGenerating ? (
                  <div className="generating-message">
                    {t(`${translationPath}GeneratingContent`) || "Generating content..."}
                  </div>
                ) : (
                  <>
                    
                  </>
                )}
              <div>
                <h3>{t(`${translationPath}EnglishTitle`) || "English Title"}</h3>
                <input
                  type="text"
                  value={englishTitle}
                  onChange={(e) => handleEdit(e, "englishTitle")}
                  disabled={isGenerating}
                />
              </div>

              <div>
                <h3>{t(`${translationPath}EnglishDescription`) || "English Description"}</h3>
                <textarea
                  value={englishDescription}
                  onChange={(e) => handleEdit(e, "englishDescription")}
                  disabled={isGenerating}
                  data-field="englishDescription"
                />
              </div>

              <div>
                <h3>{t(`${translationPath}ArabicTitle`) || "Arabic Title"}</h3>
                <input
                  type="text"
                  value={arabicTitle}
                  onChange={(e) => handleEdit(e, "arabicTitle")}
                  disabled={isGenerating}
                  dir="rtl"
                />
              </div>

              <div>
                <h3>{t(`${translationPath}ArabicDescription`) || "Arabic Description"}</h3>
                <textarea
                  value={arabicDescription}
                  onChange={(e) => handleEdit(e, "arabicDescription")}
                  disabled={isGenerating}
                  dir="rtl"
                  data-field="arabicDescription"
                />
              </div>

              <div>
                <h3>{t(`${translationPath}LocalAreaDescription`) || "Local Area Description"}</h3>
                <textarea
                  value={localAreaDescription}
                  onChange={(e) => handleEdit(e, "localAreaDescription")}
                  disabled={isGenerating}
                />
              </div>

              <div>
                <h3>{t(`${translationPath}DeveloperDescription`) || "Developer Description"}</h3>
                <textarea
                  value={developerDescription}
                  onChange={(e) => handleEdit(e, "developerDescription")}
                  disabled={isGenerating}
                />
              </div>

              <div>
                <h3>{t(`${translationPath}EnglishAddress`) || "English Address"}</h3>
                <input
                  type="text"
                  value={englishAddress}
                  onChange={(e) => handleEdit(e, "englishAddress")}
                  disabled={isGenerating}
                />
              </div>

              <div>
                <h3>{t(`${translationPath}ArabicAddress`) || "Arabic Address"}</h3>
                <input
                  type="text"
                  value={arabicAddress}
                  onChange={(e) => handleEdit(e, "arabicAddress")}
                  disabled={isGenerating}
                  dir="rtl"
                />
              </div>

              <div>
                <h3>{t(`${translationPath}AdditionalTextSuggestions`) || "Additional Text Suggestions"}</h3>
                <textarea
                  value={additionalTextSuggestions}
                  onChange={(e) => handleEdit(e, "additionalTextSuggestions")}
                  disabled={isGenerating}
                />
              </div>

              <div className="modal-actions">
                {isGenerating ? (
                  <div className="generating-message">
                   
                  </div>
                ) : (
                  <>
                    {showRegenerate && (
                      <button
                        onClick={handleRegenerate}
                        disabled={!isGenerationComplete}
                      >
                        {t(`${translationPath}Regenerate`) || "Regenerate"}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        }
        saveType="button"
        onSaveClicked={handleSave}
        saveIsDisabled={!isGenerationComplete}
        onCloseClicked={onClose}
        onCancelClicked={onClose}
      />
    </>
  );
};

export default AIPropertyMarketingContentGeneratorComponent;
 
